class MartilloHeader extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.openButton = this.querySelector('[data-mt-menu-open]');
    this.closeButton = this.querySelector('[data-mt-menu-close]');
    this.drawer = this.querySelector('[data-mt-mobile-menu]');
    this.backdrop = this.querySelector('[data-mt-menu-backdrop]');
    this.productTrigger = this.querySelector('[data-mt-products-trigger]');
    this.productMenu = this.querySelector('[data-mt-products-menu]');

    this.openButton?.addEventListener('click', () => this.openMenu(), { signal });
    this.closeButton?.addEventListener('click', () => this.closeMenu(), { signal });
    this.backdrop?.addEventListener('click', () => this.closeMenu(), { signal });
    this.drawer?.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => this.closeMenu(false), { signal }));
    this.productTrigger?.addEventListener('click', () => this.toggleProducts(), { signal });
    document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    document.addEventListener('click', (event) => this.onDocumentClick(event), { signal });
    this.querySelectorAll('details').forEach((details) => {
      const summary = details.querySelector(':scope > summary');
      const sync = () => summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      sync();
      details.addEventListener('toggle', sync, { signal });
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    document.body.classList.remove('mt-menu-open');
  }

  openMenu() {
    if (!this.drawer || !this.backdrop) return;
    this.previouslyFocused = document.activeElement;
    this.backdrop.hidden = false;
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mt-menu-open');
    this.openButton?.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      this.drawer.classList.add('is-open');
      this.backdrop.classList.add('is-open');
      this.closeButton?.focus();
    });
  }

  closeMenu(returnFocus = true) {
    if (!this.drawer || !this.backdrop) return;
    this.drawer.classList.remove('is-open');
    this.backdrop.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mt-menu-open');
    this.openButton?.setAttribute('aria-expanded', 'false');
    window.setTimeout(() => {
      if (!this.drawer?.classList.contains('is-open')) this.backdrop.hidden = true;
    }, 180);
    if (returnFocus && this.previouslyFocused instanceof HTMLElement) this.previouslyFocused.focus();
  }

  toggleProducts(force) {
    if (!this.productTrigger || !this.productMenu) return;
    const expanded = force ?? this.productTrigger.getAttribute('aria-expanded') !== 'true';
    this.productTrigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    this.productMenu.classList.toggle('is-open', expanded);
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    if (this.drawer?.classList.contains('is-open')) {
      this.closeMenu();
      return;
    }
    if (this.productTrigger?.getAttribute('aria-expanded') === 'true') {
      this.toggleProducts(false);
      this.productTrigger.focus();
    }
  }

  onDocumentClick(event) {
    if (!(event.target instanceof Element) || event.target.closest('martillo-header') === this) {
      if (event.target instanceof Element && event.target.closest('.mt-header__dropdown')) return;
    }
    this.toggleProducts(false);
  }
}

class MartilloCarousel extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.slides = [...this.querySelectorAll('[data-mt-slide]')];
    this.dots = [...this.querySelectorAll('[data-mt-carousel-dot]')];
    this.previousButton = this.querySelector('[data-mt-carousel-previous]');
    this.nextButton = this.querySelector('[data-mt-carousel-next]');
    this.pauseButton = this.querySelector('[data-mt-carousel-pause]');
    this.status = this.querySelector('[data-mt-carousel-status]');
    this.viewport = this.querySelector('[data-mt-carousel-viewport]');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.duration = Number(this.dataset.duration) || 6500;
    this.currentSlide = 0;
    this.progressFrame = null;
    this.progressElapsed = 0;
    this.progressStartedAt = 0;
    this.userPaused = false;
    this.dragging = false;
    this.focusPaused = false;
    this.dragStart = null;

    this.previousButton?.addEventListener('click', () => this.showSlide(this.currentSlide - 1), { signal });
    this.nextButton?.addEventListener('click', () => this.showSlide(this.currentSlide + 1), { signal });
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.showSlide(Number(dot.dataset.mtCarouselDot)), { signal }));
    this.pauseButton?.addEventListener('click', () => {
      this.userPaused = !this.userPaused;
      this.syncPauseButton();
      this.syncPlayback();
    }, { signal });
    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.addEventListener('focusin', () => { this.focusPaused = true; this.syncPlayback(); }, { signal });
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
      this.focusPaused = false;
      this.syncPlayback();
    }, { signal });
    this.viewport?.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
    this.viewport?.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
    this.viewport?.addEventListener('pointercancel', () => this.cancelDrag(), { signal });
    document.addEventListener('visibilitychange', () => this.syncPlayback(), { signal });
    this.reducedMotion.addEventListener?.('change', () => {
      this.progressElapsed = 0;
      this.syncPlayback();
    }, { signal });
    this.showSlide(0, false, true);
  }

  disconnectedCallback() {
    this.stopProgress(false);
    this.controller?.abort();
    this.controller = null;
  }

  renderProgress(value) {
    this.style.setProperty('--mt-carousel-progress', Math.min(1, Math.max(0, value)).toFixed(4));
  }

  stopProgress(preserve = true) {
    if (this.progressFrame !== null && preserve) {
      this.progressElapsed = Math.min(this.duration, Math.max(0, performance.now() - this.progressStartedAt));
    }
    if (this.progressFrame !== null) cancelAnimationFrame(this.progressFrame);
    this.progressFrame = null;
  }

  playbackBlocked() {
    return this.userPaused || this.dragging || this.focusPaused || this.reducedMotion.matches || document.hidden;
  }

  syncPlayback() {
    if (this.slides.length < 2) {
      this.stopProgress(false);
      this.renderProgress(1);
      this.dataset.autoplayState = 'disabled';
      return;
    }
    if (this.reducedMotion.matches) {
      this.stopProgress(false);
      this.progressElapsed = 0;
      this.renderProgress(1);
      this.dataset.autoplayState = 'reduced-motion';
      return;
    }
    if (this.playbackBlocked()) {
      this.stopProgress(true);
      this.renderProgress(this.progressElapsed / this.duration);
      this.dataset.autoplayState = 'paused';
      return;
    }
    if (this.progressFrame !== null) return;
    this.progressStartedAt = performance.now() - this.progressElapsed;
    this.dataset.autoplayState = 'running';
    const tick = (now) => {
      this.progressElapsed = Math.min(this.duration, Math.max(0, now - this.progressStartedAt));
      this.renderProgress(this.progressElapsed / this.duration);
      if (this.progressElapsed >= this.duration) {
        this.progressFrame = null;
        this.progressElapsed = 0;
        this.showSlide(this.currentSlide + 1, false, true);
        return;
      }
      this.progressFrame = requestAnimationFrame(tick);
    };
    this.progressFrame = requestAnimationFrame(tick);
  }

  syncPauseButton() {
    this.pauseButton?.classList.toggle('is-paused', this.userPaused);
    this.pauseButton?.setAttribute('aria-label', this.userPaused ? 'Play carousel' : 'Pause carousel');
  }

  showSlide(index, announce = true, resetProgress = true) {
    if (!this.slides.length) return;
    this.stopProgress(false);
    if (resetProgress) this.progressElapsed = 0;
    this.currentSlide = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.currentSlide;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.inert = !active;
    });
    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === this.currentSlide;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
    if (announce && this.status) this.status.textContent = `Showing product ${this.currentSlide + 1} of ${this.slides.length}`;
    this.renderProgress(this.reducedMotion.matches ? 1 : this.progressElapsed / this.duration);
    this.syncPlayback();
  }

  onKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showSlide(this.currentSlide - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showSlide(this.currentSlide + 1);
    }
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('a, button, input, select, textarea, [role="button"]')) return;
    this.dragStart = event.clientX;
    this.dragging = true;
    this.syncPlayback();
    this.viewport?.setPointerCapture?.(event.pointerId);
  }

  onPointerUp(event) {
    if (this.dragStart === null) return;
    const distance = event.clientX - this.dragStart;
    this.dragStart = null;
    this.dragging = false;
    if (Math.abs(distance) > 44) this.showSlide(distance > 0 ? this.currentSlide - 1 : this.currentSlide + 1);
    else this.syncPlayback();
  }

  cancelDrag() {
    this.dragStart = null;
    this.dragging = false;
    this.syncPlayback();
  }

  reviewState() {
    return {
      currentSlide: this.currentSlide,
      duration: this.duration,
      progress: this.progressElapsed / this.duration,
      running: this.progressFrame !== null,
      userPaused: this.userPaused,
      reducedMotion: this.reducedMotion.matches,
    };
  }
}

if (!customElements.get('martillo-header')) customElements.define('martillo-header', MartilloHeader);
if (!customElements.get('martillo-carousel')) customElements.define('martillo-carousel', MartilloCarousel);

window.martilloReview = {
  get carousel() { return document.querySelector('martillo-carousel'); },
  showSlide(index) { this.carousel?.showSlide(Number(index), false, true); },
  getCarouselState() { return this.carousel?.reviewState() ?? null; },
  openMenu() { document.querySelector('martillo-header')?.openMenu(); },
  closeMenu() { document.querySelector('martillo-header')?.closeMenu(false); },
};
